import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Text } from '../../design/text';
import LoadingView from '../../components/LoadingView';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface YearlyMetric {
  year: number;
  metrics: PerformanceMetrics;
}

interface PerformanceMetrics {
  totalLots: number;
  soldLots: number;
  unsoldLots: number;
  sellThroughRate: number;
  totalHammerValue: number;
  avgHammerPrice: number;
  performanceVsEstimate: number;
  marketStrength: string;
}

interface CategoryMetric {
  category: string;
  metrics: PerformanceMetrics;
}

interface TopSale {
  id: string;
  title: string;
  artist: string | null;
  artistId: string | null;
  price: number;
  year: number;
  saleDate: string;
  imageUrl: string;
}

interface AuctionHouseData {
  id: string;
  name: string;
  slug: string;
  overallMetrics: PerformanceMetrics;
  yearlyMetrics: YearlyMetric[];
  categories: CategoryMetric[];
  topSales: TopSale[];
}

const formatCurrency = (value: number): string => {
  if (value >= 1000000000) {
    return `$${(value / 1000000000).toFixed(1)}B`;
  } else if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}K`;
  }
  return `$${value.toLocaleString()}`;
};

const getStrengthStyles = (strength: string): string => {
  switch (strength.toLowerCase()) {
    case 'exceptional':
      return 'bg-emerald-50 text-emerald-800 border border-emerald-200';
    case 'strong':
      return 'bg-blue-50 text-blue-800 border border-blue-200';
    case 'moderate':
      return 'bg-amber-50 text-amber-800 border border-amber-200';
    case 'fair':
      return 'bg-rose-50 text-rose-800 border border-rose-200';
    default:
      return 'bg-gallery-100 text-gallery-600 border border-gallery-200';
  }
};

// Refined chart color palette
const chartColors = {
  primary: 'rgb(41, 37, 36)',       // gallery-800
  primaryLight: 'rgba(41, 37, 36, 0.1)',
  secondary: 'rgb(120, 113, 108)',   // gallery-500
  success: 'rgb(5, 150, 105)',       // emerald-600
  successLight: 'rgba(5, 150, 105, 0.1)',
  danger: 'rgb(225, 29, 72)',        // rose-600
  muted: 'rgb(231, 229, 228)',       // gallery-200
  accent: 'rgb(180, 83, 9)',         // amber-700
};

const AuctionHouseDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [houseData, setHouseData] = useState<AuctionHouseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHouseData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/auction-houses/${slug}`);
        setHouseData(response.data?.data || response.data || null);
      } catch (err) {
        setError('Failed to load auction house data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchHouseData();
  }, [slug]);

  if (loading) {
    return <LoadingView />;
  }

  if (error || !houseData) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Text className="text-rose-600">{error || 'Auction house not found'}</Text>
        </div>
      </div>
    );
  }

  const { name, overallMetrics, yearlyMetrics, categories, topSales } = houseData;

  // Chart configurations with refined styling
  const baseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgb(41, 37, 36)',
        titleFont: { family: 'Inter', size: 12, weight: 'bold' as const },
        bodyFont: { family: 'Inter', size: 11 },
        padding: 12,
        cornerRadius: 4,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { family: 'Inter', size: 11 },
          color: 'rgb(120, 113, 108)',
        },
        border: { display: false },
      },
      y: {
        grid: {
          color: 'rgb(245, 245, 244)',
          drawBorder: false,
        },
        ticks: {
          font: { family: 'Inter', size: 11 },
          color: 'rgb(120, 113, 108)',
        },
        border: { display: false },
      },
    },
  };

  const salesTrendData = {
    labels: yearlyMetrics.map((ym) => ym.year),
    datasets: [
      {
        label: 'Total Sales ($)',
        data: yearlyMetrics.map((ym) => ym.metrics.totalHammerValue),
        borderColor: chartColors.primary,
        backgroundColor: chartColors.primaryLight,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: chartColors.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const sellThroughData = {
    labels: yearlyMetrics.map((ym) => ym.year),
    datasets: [
      {
        label: 'Sell-Through Rate (%)',
        data: yearlyMetrics.map((ym) => ym.metrics.sellThroughRate),
        borderColor: chartColors.success,
        backgroundColor: chartColors.successLight,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: chartColors.success,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const avgPriceData = {
    labels: yearlyMetrics.map((ym) => ym.year),
    datasets: [
      {
        label: 'Average Hammer Price ($)',
        data: yearlyMetrics.map((ym) => ym.metrics.avgHammerPrice),
        backgroundColor: chartColors.secondary,
        borderRadius: 4,
        maxBarThickness: 40,
      },
    ],
  };

  const performanceData = {
    labels: yearlyMetrics.map((ym) => ym.year),
    datasets: [
      {
        label: 'Performance vs Estimate (%)',
        data: yearlyMetrics.map((ym) => ym.metrics.performanceVsEstimate),
        backgroundColor: yearlyMetrics.map((ym) =>
          ym.metrics.performanceVsEstimate > 0 ? chartColors.success : chartColors.danger
        ),
        borderRadius: 4,
        maxBarThickness: 40,
      },
    ],
  };

  const volumeData = {
    labels: yearlyMetrics.map((ym) => ym.year),
    datasets: [
      {
        label: 'Sold',
        data: yearlyMetrics.map((ym) => ym.metrics.soldLots),
        backgroundColor: chartColors.primary,
        borderRadius: 4,
      },
      {
        label: 'Unsold',
        data: yearlyMetrics.map((ym) => ym.metrics.unsoldLots),
        backgroundColor: chartColors.muted,
        borderRadius: 4,
      },
    ],
  };

  const categoryDistributionData = {
    labels: categories.map((c) => c.category || 'Unknown'),
    datasets: [
      {
        data: categories.map((c) => c.metrics.totalLots),
        backgroundColor: [
          'rgb(41, 37, 36)',
          'rgb(68, 64, 60)',
          'rgb(87, 83, 78)',
          'rgb(120, 113, 108)',
          'rgb(168, 162, 158)',
          'rgb(214, 211, 209)',
          'rgb(180, 83, 9)',
          'rgb(5, 150, 105)',
          'rgb(37, 99, 235)',
          'rgb(124, 58, 237)',
        ],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          font: { family: 'Inter', size: 11 },
          color: 'rgb(68, 64, 60)',
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: 'rgb(41, 37, 36)',
        titleFont: { family: 'Inter', size: 12, weight: 'bold' as const },
        bodyFont: { family: 'Inter', size: 11 },
        padding: 12,
        cornerRadius: 4,
      },
    },
    cutout: '65%',
  };

  return (
    <div className="min-h-screen bg-gallery-50">
      {/* Header */}
      <div className="bg-white border-b border-gallery-200">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link
            to="/auction-houses"
            className="inline-flex items-center gap-2 text-sm text-gallery-500 hover:text-gallery-700 transition-colors mb-6"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
            All Auction Houses
          </Link>
          <h1 className="font-display text-display-sm text-gallery-900 mb-2">{name}</h1>
          <p className="text-gallery-500">Comprehensive Market Performance Analysis</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {[
            { value: overallMetrics.totalLots.toLocaleString(), label: 'Total Lots' },
            { value: `${overallMetrics.sellThroughRate}%`, label: 'Sell-Through Rate' },
            { value: formatCurrency(overallMetrics.totalHammerValue), label: 'Total Sales' },
            { value: formatCurrency(overallMetrics.avgHammerPrice), label: 'Avg Hammer Price' },
            {
              value: `${overallMetrics.performanceVsEstimate > 0 ? '+' : ''}${overallMetrics.performanceVsEstimate}%`,
              label: 'vs Estimate',
              highlight: overallMetrics.performanceVsEstimate >= 0 ? 'positive' : 'negative'
            },
            { value: overallMetrics.marketStrength, label: 'Market Strength', isBadge: true },
          ].map((stat, idx) => (
            <div key={idx} className="bg-white border border-gallery-200 rounded-sm p-5">
              {stat.isBadge ? (
                <span className={`inline-block px-3 py-1.5 rounded-sm text-xs font-medium tracking-wide ${getStrengthStyles(stat.value)}`}>
                  {stat.value}
                </span>
              ) : (
                <div className={`stat-value ${
                  stat.highlight === 'positive' ? 'text-emerald-700' :
                  stat.highlight === 'negative' ? 'text-rose-700' : ''
                }`}>
                  {stat.value}
                </div>
              )}
              <div className="stat-label mt-2">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <div className="bg-white border border-gallery-200 rounded-sm p-6">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Year-over-Year Total Sales</h3>
            <div className="h-72">
              <Line data={salesTrendData} options={baseChartOptions} />
            </div>
          </div>

          <div className="bg-white border border-gallery-200 rounded-sm p-6">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Sell-Through Rate by Year</h3>
            <div className="h-72">
              <Line data={sellThroughData} options={baseChartOptions} />
            </div>
          </div>

          <div className="bg-white border border-gallery-200 rounded-sm p-6">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Average Hammer Price Trend</h3>
            <div className="h-72">
              <Bar data={avgPriceData} options={baseChartOptions} />
            </div>
          </div>

          <div className="bg-white border border-gallery-200 rounded-sm p-6">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Performance vs Estimate by Year</h3>
            <div className="h-72">
              <Bar data={performanceData} options={baseChartOptions} />
            </div>
          </div>
        </div>

        {/* Volume Trend */}
        <div className="bg-white border border-gallery-200 rounded-sm p-6 mb-10">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-lg text-gallery-900">Lots Sold by Year</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-gallery-800"></div>
                <span className="text-xs text-gallery-500">Sold</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-gallery-200"></div>
                <span className="text-xs text-gallery-500">Unsold</span>
              </div>
            </div>
          </div>
          <div className="h-72">
            <Bar
              data={volumeData}
              options={{
                ...baseChartOptions,
                plugins: {
                  ...baseChartOptions.plugins,
                  legend: { display: false },
                },
                scales: {
                  ...baseChartOptions.scales,
                  x: { ...baseChartOptions.scales.x, stacked: true },
                  y: { ...baseChartOptions.scales.y, stacked: true, beginAtZero: true },
                },
              }}
            />
          </div>
        </div>

        {/* Category Analysis */}
        {categories.length > 0 && (
          <div className="bg-white border border-gallery-200 rounded-sm p-6 mb-10">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Category Distribution</h3>
            <div className="h-80">
              <Doughnut data={categoryDistributionData} options={doughnutOptions} />
            </div>
          </div>
        )}

        {/* Top Sales */}
        {topSales.length > 0 && (
          <div className="bg-white border border-gallery-200 rounded-sm p-6 mb-10">
            <h3 className="font-display text-lg text-gallery-900 mb-6">Notable Sales</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {topSales.slice(0, 20).map((sale) => (
                <Link
                  key={sale.id}
                  to={`/auction-record/${sale.id}`}
                  className="group block p-4 border border-gallery-200 rounded-sm hover:border-gallery-400 transition-colors"
                >
                  <div className="stat-value text-xl mb-2">
                    ${sale.price.toLocaleString()}
                  </div>
                  <div className="font-medium text-gallery-800 mb-1 line-clamp-2 group-hover:text-gallery-600 transition-colors">
                    {sale.title}
                  </div>
                  {sale.artist && (
                    <div className="text-sm text-gallery-500 mb-1">{sale.artist}</div>
                  )}
                  <div className="text-xs text-gallery-400">
                    {sale.year && <span>c. {sale.year}</span>}
                    {sale.year && sale.saleDate && <span className="mx-1">·</span>}
                    {sale.saleDate && <span>{new Date(sale.saleDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Yearly Performance Table */}
        <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-gallery-100">
            <h3 className="font-display text-lg text-gallery-900">Yearly Performance Breakdown</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gallery-50 border-b border-gallery-200">
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Year</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Total Lots</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Sold</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Sell-Through</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Total Sales</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Avg Price</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">vs Estimate</th>
                  <th className="px-6 py-4 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Strength</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gallery-100">
                {yearlyMetrics.map((ym) => (
                  <tr key={ym.year} className="hover:bg-gallery-50 transition-colors">
                    <td className="px-6 py-4 font-display text-gallery-900">{ym.year}</td>
                    <td className="px-6 py-4 text-gallery-700">{ym.metrics.totalLots.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gallery-700">{ym.metrics.soldLots.toLocaleString()}</td>
                    <td className="px-6 py-4 text-gallery-700">{ym.metrics.sellThroughRate}%</td>
                    <td className="px-6 py-4 text-gallery-700">{formatCurrency(ym.metrics.totalHammerValue)}</td>
                    <td className="px-6 py-4 text-gallery-700">{formatCurrency(ym.metrics.avgHammerPrice)}</td>
                    <td className={`px-6 py-4 font-medium ${
                      ym.metrics.performanceVsEstimate > 0 ? 'text-emerald-700' : 'text-rose-700'
                    }`}>
                      {ym.metrics.performanceVsEstimate > 0 ? '+' : ''}
                      {ym.metrics.performanceVsEstimate}%
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-sm text-xs font-medium ${getStrengthStyles(ym.metrics.marketStrength)}`}>
                        {ym.metrics.marketStrength}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuctionHouseDetail;
